import { useState } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import { T, useGT } from 'gt-react';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toast';
import {
  useArCashReceipt,
  useDeleteCashReceipt,
  useUnlinkCashReceipt,
  useRematchCashReceipt,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import {
  OriginReadOnlyBadge,
  isEditableCashReceiptOrigin,
} from '@/components/common/OriginReadOnlyBadge';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { CashReceiptCustomerSection } from './cash-receipt-customer-section';
import { CashReceiptPaymentSection } from './cash-receipt-payment-section';
import { CashReceiptBankSection } from './cash-receipt-bank-section';
import { CashReceiptFormDialog } from './CashReceiptFormDialog';

function formatAmount(amount: string | null): string {
  if (amount === null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

function StatusBadge({ status }: { status: string }) {
  const gt = useGT();
  if (status === 'needs_review') {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200">
        <T context="AR cash receipt matching status">Needs Review</T>
      </Badge>
    );
  }
  if (status === 'matched') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-800 border-green-200"
      >
        <T context="AR cash receipt matching status">Matched</T>
      </Badge>
    );
  }
  return (
    <Badge variant="outline">
      {gt('{status}', {
        context:
          'AR cash receipt status label when value is not a known preset',
        status,
      })}
    </Badge>
  );
}

interface CashReceiptDetailSlideinProps {
  receiptId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CashReceiptDetailSlidein({
  receiptId,
  isOpen,
  onOpenChange,
}: CashReceiptDetailSlideinProps) {
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('ar-cash-receipt-detail-view-size', {
      defaultValue: 50,
    });
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'ar-cash-receipt-details-panel-size',
    { defaultValue: 50, initializeWithValue: true },
  );

  const gt = useGT();
  const { receipt, isLoading, isError } = useArCashReceipt(receiptId);
  const deleteMutation = useDeleteCashReceipt();
  const unlinkMutation = useUnlinkCashReceipt();
  const rematchMutation = useRematchCashReceipt();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const editable = receipt
    ? isEditableCashReceiptOrigin(receipt.originSystem)
    : false;

  async function handleRematch() {
    try {
      const { receipt: updated } = await rematchMutation.mutateAsync({
        id: receiptId,
      });
      if (updated.status === 'matched') {
        toast.success(
          gt('Matched to a payment.', { context: 'AR cash receipt re-match' }),
        );
      } else {
        toast.info(
          gt('No matching payment found for this receipt.', {
            context: 'AR cash receipt re-match, no result',
          }),
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Failed to re-match: ${err.message}`
          : 'Failed to re-match cash receipt',
      );
    }
  }

  async function handleUnlink() {
    try {
      await unlinkMutation.mutateAsync({ id: receiptId });
      toast.success(
        gt('Unlinked from payment.', { context: 'AR cash receipt unlink' }),
      );
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Failed to unlink: ${err.message}`
          : 'Failed to unlink cash receipt',
      );
    }
  }

  async function handleConfirmDelete() {
    try {
      await deleteMutation.mutateAsync({ id: receiptId });
      setConfirmDeleteOpen(false);
      onOpenChange(false);
    } catch (err) {
      // Surface the failure via toast and keep the dialog open so the
      // operator can retry. mutateAsync rethrows here even though the
      // mutation also captures `error` in its own state.
      toast.error(
        err instanceof Error
          ? `Failed to delete cash receipt: ${err.message}`
          : 'Failed to delete cash receipt',
      );
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContentResizable
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        <SheetHeader className="shrink-0 grow-0">
          {isLoading ? (
            <SheetTitle>
              <T context="loading state for AR cash receipt detail">
                Loading...
              </T>
            </SheetTitle>
          ) : receipt ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-2xl font-bold">
                  {formatAmount(receipt.paymentAmount)}
                </SheetTitle>
                <StatusBadge status={receipt.status} />
                {!editable && <OriginReadOnlyBadge />}
              </div>
              <p className="text-sm text-muted-foreground">
                {formatDate(receipt.paymentDate)}
              </p>
            </div>
          ) : (
            <SheetTitle>
              <T context="AR cash receipt detail when receipt id is unknown">
                Receipt not found
              </T>
            </SheetTitle>
          )}
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="loading state for AR cash receipt detail body">
                Loading...
              </T>
            </div>
          ) : isError ? (
            <div className="p-4 text-sm text-destructive">
              <T context="error loading AR cash receipt detail">
                Failed to load receipt details. Please try again.
              </T>
            </div>
          ) : receipt ? (
            receipt.sourceEmail ? (
              <ResizablePanelGroup
                direction="horizontal"
                className="h-full gap-2 px-3"
              >
                <ResizablePanel
                  defaultSize={panelSize}
                  onResize={setPanelSize}
                  minSize={25}
                  className="flex flex-col pr-2"
                >
                  <div className="flex-1 min-h-0 divide-y overflow-y-auto">
                    <div className="py-4">
                      <CashReceiptCustomerSection receipt={receipt} />
                    </div>
                    <div className="py-4">
                      <CashReceiptPaymentSection receipt={receipt} />
                    </div>
                    <div className="py-4">
                      <CashReceiptBankSection receipt={receipt} />
                    </div>
                  </div>
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel minSize={20}>
                  <SmartEmailViewer
                    emailData={inboundEmailToEmailData(receipt.sourceEmail)}
                    conversationId={receipt.sourceEmail.conversationId}
                    defaultTab="email"
                    sourceEmailId={receipt.sourceEmail.id}
                    highlightedEmailIds={[receipt.sourceEmail.id]}
                  />
                </ResizablePanel>
              </ResizablePanelGroup>
            ) : (
              <div className="divide-y">
                <div className="p-4">
                  <CashReceiptCustomerSection receipt={receipt} />
                </div>
                <div className="p-4">
                  <CashReceiptPaymentSection receipt={receipt} />
                </div>
                <div className="p-4">
                  <CashReceiptBankSection receipt={receipt} />
                </div>
              </div>
            )
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="AR cash receipt detail when receipt is missing">
                Receipt not found
              </T>
            </div>
          )}
        </div>

        <SheetFooter className="shrink-0 grow-0 flex flex-row items-end justify-between border-t gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={!receipt || deleteMutation.isPending}
              className="w-fit"
            >
              <T context="delete AR cash receipt button">Delete</T>
            </Button>
            {editable && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                className="w-fit"
              >
                <T context="edit AR cash receipt button">Edit</T>
              </Button>
            )}
            {receipt?.status === 'needs_review' && (
              <Button
                variant="outline"
                onClick={() => void handleRematch()}
                disabled={rematchMutation.isPending}
                className="w-fit"
              >
                <T context="manually run matching for AR cash receipt">
                  Re-match
                </T>
              </Button>
            )}
            {receipt?.status === 'matched' && (
              <Button
                variant="outline"
                onClick={() => void handleUnlink()}
                disabled={unlinkMutation.isPending}
                className="w-fit"
              >
                <T context="break the AR cash receipt to payment match">
                  Unlink
                </T>
              </Button>
            )}
          </div>
          <Button onClick={() => onOpenChange(false)} className="w-fit">
            <T context="close slide-in panel">Close</T>
          </Button>
        </SheetFooter>
      </SheetContentResizable>

      {receipt && editable && (
        <CashReceiptFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          receipt={receipt}
        />
      )}

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="confirm deleting an AR cash receipt">
                Delete this cash receipt?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="explanation of AR cash receipt deletion side effects">
                This permanently removes the bank transaction and unlinks it
                from any matched payment. Use this for receipts that were
                created incorrectly. This action cannot be undone.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              <T context="AR cash receipt destructive confirm">
                Delete Permanently
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
